// Exercise name templates for different categories
const exerciseTemplates = {
  strength: {
    prefixes: ['Barbell', 'Dumbbell', 'Kettlebell', 'Cable', 'Machine', 'Bodyweight', 'Resistance Band', 'Medicine Ball', 'TRX', 'Ring'],
    movements: ['Press', 'Row', 'Squat', 'Deadlift', 'Lunge', 'Curl', 'Extension', 'Raise', 'Pull', 'Push', 'Dip', 'Pull-up', 'Crunch', 'Plank'],
    targets: ['Chest', 'Back', 'Shoulders', 'Biceps', 'Triceps', 'Quadriceps', 'Hamstrings', 'Glutes', 'Calves', 'Core', 'Forearms', 'Traps', 'Lats'],
    modifiers: ['Single Arm', 'Single Leg', 'Alternating', 'Pulse', 'Hold', 'Explosive', 'Slow', 'Isometric', 'Eccentric', 'Concentric']
  },
  cardio: {
    prefixes: ['High Intensity', 'Low Impact', 'Steady State', 'Interval', 'Tabata', 'Circuit', 'Endurance', 'Speed', 'Agility', 'Recovery'],
    movements: ['Run', 'Walk', 'Jump', 'Skip', 'Hop', 'March', 'Kick', 'Punch', 'Swing', 'Twist', 'Shuffle', 'Sprint', 'Jog', 'Gallop'],
    targets: ['Cardiovascular', 'Full Body', 'Lower Body', 'Upper Body', 'Core', 'Coordination', 'Balance', 'Speed', 'Endurance', 'Agility'],
    modifiers: ['Fast', 'Slow', 'Alternating', 'Continuous', 'Burst', 'Steady', 'Progressive', 'Declining', 'Random', 'Structured']
  },
  flexibility: {
    prefixes: ['Dynamic', 'Static', 'Active', 'Passive', 'PNF', 'Ballistic', 'Yoga', 'Pilates', 'Stretch', 'Mobility'],
    movements: ['Stretch', 'Hold', 'Flow', 'Pose', 'Twist', 'Bend', 'Reach', 'Open', 'Release', 'Extend', 'Rotate', 'Flex'],
    targets: ['Hamstrings', 'Quadriceps', 'Hip Flexors', 'Calves', 'Chest', 'Shoulders', 'Back', 'Neck', 'Ankles', 'Wrists', 'Spine', 'Groin'],
    modifiers: ['Deep', 'Gentle', 'Long Hold', 'Quick', 'Rhythmic', 'Breathing', 'Partner', 'Assisted', 'Resistance', 'Gravity']
  },
  balance: {
    prefixes: ['Single Leg', 'Dynamic', 'Static', 'Progressive', 'Advanced', 'Beginner', 'Sport Specific', 'Functional', 'Core Focused', 'Multi Plane'],
    movements: ['Stand', 'Hold', 'Reach', 'Squat', 'Lunge', 'Twist', 'Bend', 'Rotate', 'Step', 'Walk', 'Balance', 'Stabilize'],
    targets: ['Core', 'Ankles', 'Hips', 'Knees', 'Full Body', 'Lower Body', 'Coordination', 'Proprioception', 'Stability', 'Control'],
    modifiers: ['Eyes Closed', 'Head Turn', 'Arm Movement', 'Leg Movement', 'Unstable Surface', 'Weight Shift', 'Direction Change', 'Speed Variation']
  }
};

// Generate random exercise names
const generateExerciseName = (category: string): string => {
  const template = exerciseTemplates[category as keyof typeof exerciseTemplates];
  if (!template) return 'Custom Exercise';

  const prefix = template.prefixes[Math.floor(Math.random() * template.prefixes.length)];
  const movement = template.movements[Math.floor(Math.random() * template.movements.length)];
  const target = template.targets[Math.floor(Math.random() * template.targets.length)];
  const modifier = Math.random() > 0.7 ? template.modifiers[Math.floor(Math.random() * template.modifiers.length)] : '';

  if (modifier) {
    return `${modifier} ${prefix} ${target} ${movement}`;
  }
  return `${prefix} ${target} ${movement}`;
};

// Generate random descriptions
const generateDescription = (name: string, category: string): string => {
  const descriptions = {
    strength: [
      `A powerful ${category} exercise that targets multiple muscle groups for maximum strength gains.`,
      `Build functional strength with this effective ${category} movement that engages your core.`,
      `This ${category} exercise is perfect for building muscle and improving overall fitness.`,
      `A compound ${category} movement that works multiple joints and muscle groups simultaneously.`,
      `Strengthen your ${category} with this challenging exercise that improves stability and power.`
    ],
    cardio: [
      `Get your heart pumping with this high-energy ${category} exercise that burns calories fast.`,
      `Improve cardiovascular fitness with this dynamic ${category} movement that boosts endurance.`,
      `This ${category} exercise is perfect for warming up or getting a quick cardio blast.`,
      `Build stamina and coordination with this engaging ${category} workout.`,
      `Elevate your heart rate with this effective ${category} exercise that targets multiple energy systems.`
    ],
    flexibility: [
      `Improve your ${category} flexibility with this gentle stretch that promotes mobility.`,
      `This ${category} exercise helps release tension and increase your range of motion.`,
      `Enhance your ${category} with this relaxing stretch that promotes recovery.`,
      `A therapeutic ${category} movement that improves posture and reduces stiffness.`,
      `This ${category} exercise is perfect for cooling down and improving flexibility.`
    ],
    balance: [
      `Challenge your ${category} with this balance exercise that improves stability and coordination.`,
      `This ${category} exercise enhances proprioception and core strength.`,
      `Improve your ${category} with this functional movement that builds body awareness.`,
      `A challenging ${category} exercise that strengthens stabilizing muscles.`,
      `This ${category} movement improves coordination and reduces fall risk.`
    ]
  };

  const categoryDescriptions = descriptions[category as keyof typeof descriptions] || descriptions.strength;
  return categoryDescriptions[Math.floor(Math.random() * categoryDescriptions.length)];
};

// Generate random instructions
const generateInstructions = (category: string): string[] => {
  const baseInstructions = {
    strength: [
      'Start in the proper position with good posture',
      'Engage your core muscles throughout the movement',
      'Move through the full range of motion with control',
      'Maintain proper form and breathing pattern',
      'Complete the desired number of repetitions'
    ],
    cardio: [
      'Begin in a comfortable starting position',
      'Start at a moderate pace and gradually increase intensity',
      'Maintain steady breathing throughout the exercise',
      'Keep good form even as you increase speed',
      'Continue for the recommended duration'
    ],
    flexibility: [
      'Find a comfortable position to begin',
      'Breathe deeply and relax into the stretch',
      'Hold the position without forcing or bouncing',
      'Feel the stretch in the target muscle group',
      'Maintain the stretch for the recommended time'
    ],
    balance: [
      'Find a stable surface and clear the area around you',
      'Focus on a fixed point in front of you',
      'Engage your core muscles for stability',
      'Maintain the position with controlled breathing',
      'Gradually increase difficulty as you improve'
    ]
  };

  const instructions = baseInstructions[category as keyof typeof baseInstructions] || baseInstructions.strength;
  return instructions.slice(0, 3 + Math.floor(Math.random() * 3)); // 3-5 instructions
};

// Generate random tips
const generateTips = (): string[] => {
  const allTips = [
    'Keep your core engaged throughout the movement',
    'Maintain proper breathing pattern',
    'Don\'t rush the movement - focus on form',
    'Listen to your body and stop if you feel pain',
    'Gradually increase intensity over time',
    'Warm up properly before starting',
    'Stay hydrated during your workout',
    'Focus on the mind-muscle connection',
    'Don\'t let your form break down',
    'Use a mirror to check your form',
    'Start with lighter weights to perfect technique',
    'Include this exercise in your regular routine',
    'Pair with complementary exercises',
    'Allow adequate rest between sets',
    'Progress gradually to avoid injury'
  ];

  const numTips = 2 + Math.floor(Math.random() * 3); // 2-4 tips
  const shuffled = allTips.sort(() => 0.5 - Math.random());
  return shuffled.slice(0, numTips);
};

// Generate random variations
const generateVariations = (category: string): string[] => {
  const variations = {
    strength: ['Increase weight', 'Add resistance bands', 'Single limb version', 'Pause at bottom', 'Explosive concentric'],
    cardio: ['Increase speed', 'Add intervals', 'Change direction', 'Include arm movements', 'Add resistance'],
    flexibility: ['Hold longer', 'Go deeper', 'Add movement', 'Use props', 'Partner assisted'],
    balance: ['Close eyes', 'Add movement', 'Unstable surface', 'Single leg', 'Add weight']
  };

  const categoryVariations = variations[category as keyof typeof variations] || variations.strength;
  const numVariations = 2 + Math.floor(Math.random() * 3); // 2-4 variations
  const shuffled = categoryVariations.sort(() => 0.5 - Math.random());
  return shuffled.slice(0, numVariations);
};

// Fitness trainer images for exercises
const fitnessImages = [
  'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop&crop=face&auto=format',
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop&crop=face&auto=format',
  'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=400&h=300&fit=crop&crop=face&auto=format',
  'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop&crop=face&auto=format',
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop&crop=face&auto=format',
  'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=400&h=300&fit=crop&crop=face&auto=format',
  'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop&crop=face&auto=format',
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop&crop=face&auto=format',
  'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=400&h=300&fit=crop&crop=face&auto=format',
  'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop&crop=face&auto=format'
];

// Generate random equipment
const generateEquipment = (): string[] => {
  const equipment = ['None', 'Dumbbells', 'Barbell', 'Kettlebell', 'Resistance Bands', 'Pull-up Bar', 'Bench', 'Jump Rope', 'Yoga Mat', 'Foam Roller', 'Medicine Ball', 'Stability Ball', 'TRX'];
  const numEquipment = 1 + Math.floor(Math.random() * 2); // 1-2 equipment types
  const shuffled = equipment.sort(() => 0.5 - Math.random());
  return shuffled.slice(0, numEquipment);
};

// Generate random muscle groups
const generateMuscleGroups = (): string[] => {
  const muscleGroups = ['Full Body', 'Core', 'Upper Body', 'Lower Body', 'Chest', 'Back', 'Shoulders', 'Biceps', 'Triceps', 'Quadriceps', 'Hamstrings', 'Glutes', 'Calves', 'Cardiovascular', 'Obliques', 'Neck', 'Forearms'];
  const numGroups = 2 + Math.floor(Math.random() * 3); // 2-4 muscle groups
  const shuffled = muscleGroups.sort(() => 0.5 - Math.random());
  return shuffled.slice(0, numGroups);
};

// Generate random difficulty
const generateDifficulty = (): 'beginner' | 'intermediate' | 'advanced' => {
  const rand = Math.random();
  if (rand < 0.4) return 'beginner';
  if (rand < 0.8) return 'intermediate';
  return 'advanced';
};

// Generate a single exercise
const generateExercise = (id: number): Exercise => {
  const categories: Array<'strength' | 'cardio' | 'flexibility' | 'balance'> = ['strength', 'cardio', 'flexibility', 'balance'];
  const category = categories[Math.floor(Math.random() * categories.length)];
  const name = generateExerciseName(category);
  
  return {
    id: `generated-${id}`,
    name,
    description: generateDescription(name, category),
    category,
    muscleGroups: generateMuscleGroups(),
    equipment: generateEquipment(),
    difficulty: generateDifficulty(),
    instructions: generateInstructions(category),
    tips: generateTips(),
    variations: generateVariations(category),
    imageUrl: fitnessImages[Math.floor(Math.random() * fitnessImages.length)],
    isPublic: true
  };
};

// Generate 1000 exercises
const generateExercises = (count: number): Exercise[] => {
  const exercises: Exercise[] = [];
  
  for (let i = 1; i <= count; i++) {
    exercises.push(generateExercise(i));
  }
  
  return exercises;
};

// Generate 1000 exercises
const generatedExercises = generateExercises(1000);

// Log statistics
console.log('🎯 Exercise Database Generated Successfully!');
console.log('=============================================');
console.log(`📊 Total Exercises: ${generatedExercises.length}`);
console.log(`💪 Strength: ${generatedExercises.filter(e => e.category === 'strength').length}`);
console.log(`❤️ Cardio: ${generatedExercises.filter(e => e.category === 'cardio').length}`);
console.log(`🧘 Flexibility: ${generatedExercises.filter(e => e.category === 'flexibility').length}`);
console.log(`⚖️ Balance: ${generatedExercises.filter(e => e.category === 'balance').length}`);

export interface Exercise {
  id: string;
  name: string;
  description: string;
  category: 'strength' | 'cardio' | 'flexibility' | 'balance' | 'sports';
  muscleGroups: string[];
  equipment: string[];
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  instructions: string[];
  videoUrl?: string;
  imageUrl?: string;
  tips?: string[];
  variations?: string[];
  isPublic: boolean;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  accessType?: 'system' | 'own' | 'other'; // For database view
}

// Original curated exercises
export const originalExercises: Exercise[] = [
  // STRENGTH EXERCISES
  {
    id: 'squat',
    name: 'Bodyweight Squat',
    description: 'A fundamental lower body exercise that targets multiple muscle groups.',
    category: 'strength',
    muscleGroups: ['Quadriceps', 'Glutes', 'Hamstrings', 'Core'],
    equipment: ['None'],
    difficulty: 'beginner',
    instructions: [
      'Stand with feet shoulder-width apart',
      'Keep your chest up and core engaged',
      'Lower your body by bending at the knees and hips',
      'Go as low as you can while keeping heels on the ground',
      'Push through your heels to return to starting position'
    ],
    tips: ['Keep knees in line with toes', 'Don\'t let knees cave inward'],
    variations: ['Jump Squat', 'Pistol Squat', 'Wall Squat'],
    imageUrl: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop&crop=face&auto=format',
    isPublic: true
  },
  {
    id: 'pushup',
    name: 'Push-up',
    description: 'A classic upper body exercise that builds chest, shoulder, and tricep strength.',
    category: 'strength',
    muscleGroups: ['Chest', 'Shoulders', 'Triceps', 'Core'],
    equipment: ['None'],
    difficulty: 'beginner',
    instructions: [
      'Start in a plank position with hands slightly wider than shoulders',
      'Lower your body until chest nearly touches the ground',
      'Keep your body in a straight line from head to heels',
      'Push back up to starting position'
    ],
    tips: ['Keep core tight', 'Don\'t let hips sag'],
    variations: ['Knee Push-up', 'Diamond Push-up', 'Decline Push-up'],
    imageUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop&crop=face&auto=format',
    isPublic: true
  },
  {
    id: 'deadlift',
    name: 'Romanian Deadlift',
    description: 'A hip-hinge movement that primarily targets the posterior chain.',
    category: 'strength',
    muscleGroups: ['Hamstrings', 'Glutes', 'Lower Back', 'Core'],
    equipment: ['Dumbbells', 'Barbell'],
    difficulty: 'intermediate',
    instructions: [
      'Stand with feet hip-width apart, holding weights in front of thighs',
      'Hinge at the hips, pushing them back',
      'Lower weights along your legs while keeping them close',
      'Feel the stretch in your hamstrings',
      'Return to starting position by driving hips forward'
    ],
    tips: ['Keep back straight', 'Don\'t round your lower back'],
    variations: ['Single Leg RDL', 'Kettlebell RDL', 'Barbell RDL'],
    isPublic: true
  },
  {
    id: 'plank',
    name: 'Plank',
    description: 'An isometric core exercise that builds stability and endurance.',
    category: 'strength',
    muscleGroups: ['Core', 'Shoulders', 'Glutes'],
    equipment: ['None'],
    difficulty: 'beginner',
    instructions: [
      'Start in a forearm plank position',
      'Keep your body in a straight line from head to heels',
      'Engage your core and glutes',
      'Hold the position while breathing normally'
    ],
    tips: ['Don\'t let hips sag', 'Keep neck neutral'],
    variations: ['Side Plank', 'Plank with Leg Lift', 'Mountain Climber Plank'],
    isPublic: true
  },
  {
    id: 'pullup',
    name: 'Pull-up',
    description: 'An upper body pulling exercise that builds back and bicep strength.',
    category: 'strength',
    muscleGroups: ['Back', 'Biceps', 'Shoulders', 'Core'],
    equipment: ['Pull-up Bar'],
    difficulty: 'intermediate',
    instructions: [
      'Hang from pull-up bar with hands slightly wider than shoulders',
      'Pull your body up until chin is above the bar',
      'Lower yourself back down with control',
      'Repeat the movement'
    ],
    tips: ['Engage your back muscles', 'Don\'t swing your body'],
    variations: ['Assisted Pull-up', 'Negative Pull-up', 'Wide Grip Pull-up'],
    isPublic: true
  },
  {
    id: 'lunge',
    name: 'Walking Lunge',
    description: 'A dynamic lower body exercise that improves balance and coordination.',
    category: 'strength',
    muscleGroups: ['Quadriceps', 'Glutes', 'Hamstrings', 'Core'],
    equipment: ['None'],
    difficulty: 'beginner',
    instructions: [
      'Stand with feet together',
      'Step forward with one leg and lower into a lunge',
      'Both knees should be at 90-degree angles',
      'Push off the front foot to step forward with the back leg',
      'Continue alternating legs'
    ],
    tips: ['Keep chest up', 'Don\'t let front knee go past toes'],
    variations: ['Reverse Lunge', 'Side Lunge', 'Jumping Lunge'],
    isPublic: true
  },
  {
    id: 'burpee',
    name: 'Burpee',
    description: 'A full-body exercise that combines strength and cardio.',
    category: 'strength',
    muscleGroups: ['Full Body', 'Cardiovascular'],
    equipment: ['None'],
    difficulty: 'intermediate',
    instructions: [
      'Start standing, then squat down and place hands on ground',
      'Jump feet back into plank position',
      'Perform a push-up (optional)',
      'Jump feet back to squat position',
      'Jump up with arms overhead'
    ],
    tips: ['Maintain good form', 'Start slow and build speed'],
    variations: ['Half Burpee', 'Burpee with Pull-up', 'Burpee Box Jump'],
    isPublic: true
  },
  {
    id: 'mountain-climber',
    name: 'Mountain Climber',
    description: 'A dynamic core exercise that also provides cardio benefits.',
    category: 'strength',
    muscleGroups: ['Core', 'Shoulders', 'Cardiovascular'],
    equipment: ['None'],
    difficulty: 'beginner',
    instructions: [
      'Start in plank position',
      'Drive one knee toward your chest',
      'Quickly switch legs, alternating back and forth',
      'Keep your core engaged and hips level'
    ],
    tips: ['Don\'t let hips move up and down', 'Keep breathing steady'],
    variations: ['Slow Mountain Climber', 'Cross-body Mountain Climber'],
    isPublic: true
  },
  {
    id: 'dumbbell-row',
    name: 'Dumbbell Row',
    description: 'A unilateral back exercise that improves posture and back strength.',
    category: 'strength',
    muscleGroups: ['Back', 'Biceps', 'Shoulders', 'Core'],
    equipment: ['Dumbbells', 'Bench'],
    difficulty: 'beginner',
    instructions: [
      'Place one knee and hand on bench for support',
      'Hold dumbbell in opposite hand with arm extended',
      'Pull dumbbell up toward your hip',
      'Keep elbow close to body',
      'Lower with control and repeat'
    ],
    tips: ['Keep back straight', 'Don\'t rotate your torso'],
    variations: ['Standing Row', 'Bent-Over Row', 'Single-Arm Cable Row'],
    isPublic: true
  },
  {
    id: 'overhead-press',
    name: 'Overhead Press',
    description: 'A compound shoulder exercise that builds upper body strength.',
    category: 'strength',
    muscleGroups: ['Shoulders', 'Triceps', 'Core'],
    equipment: ['Dumbbells', 'Barbell'],
    difficulty: 'intermediate',
    instructions: [
      'Stand with feet shoulder-width apart',
      'Hold weights at shoulder level',
      'Press weights overhead while keeping core tight',
      'Lower weights back to shoulders with control',
      'Repeat the movement'
    ],
    tips: ['Don\'t arch your back', 'Keep core engaged'],
    variations: ['Seated Press', 'Push Press', 'Arnold Press'],
    isPublic: true
  },
  {
    id: 'dip',
    name: 'Dip',
    description: 'A bodyweight exercise that targets chest, shoulders, and triceps.',
    category: 'strength',
    muscleGroups: ['Chest', 'Shoulders', 'Triceps', 'Core'],
    equipment: ['Dip Bars', 'Parallel Bars'],
    difficulty: 'intermediate',
    instructions: [
      'Grip dip bars with arms fully extended',
      'Lower your body by bending at elbows',
      'Go as low as comfortable',
      'Push back up to starting position',
      'Keep body straight throughout movement'
    ],
    tips: ['Don\'t go too deep', 'Keep elbows close to body'],
    variations: ['Assisted Dip', 'Ring Dip', 'Weighted Dip'],
    isPublic: true
  },
  {
    id: 'wall-sit',
    name: 'Wall Sit',
    description: 'An isometric exercise that builds leg endurance and strength.',
    category: 'strength',
    muscleGroups: ['Quadriceps', 'Glutes', 'Core'],
    equipment: ['None'],
    difficulty: 'beginner',
    instructions: [
      'Lean back against a wall',
      'Slide down until thighs are parallel to ground',
      'Keep back against wall',
      'Hold position for desired time',
      'Keep feet shoulder-width apart'
    ],
    tips: ['Don\'t let knees go past toes', 'Breathe steadily'],
    variations: ['Single Leg Wall Sit', 'Wall Sit with Ball', 'Pulse Wall Sit'],
    isPublic: true
  },
  {
    id: 'calf-raise',
    name: 'Calf Raise',
    description: 'An isolation exercise that strengthens the lower leg muscles.',
    category: 'strength',
    muscleGroups: ['Calves'],
    equipment: ['None'],
    difficulty: 'beginner',
    instructions: [
      'Stand with feet shoulder-width apart',
      'Rise up onto balls of feet',
      'Hold at the top briefly',
      'Lower heels back to ground',
      'Repeat for desired reps'
    ],
    tips: ['Control the movement', 'Feel the stretch at bottom'],
    variations: ['Single Leg Calf Raise', 'Seated Calf Raise', 'Weighted Calf Raise'],
    isPublic: true
  },
  {
    id: 'glute-bridge',
    name: 'Glute Bridge',
    description: 'A hip-dominant exercise that targets the glutes and hamstrings.',
    category: 'strength',
    muscleGroups: ['Glutes', 'Hamstrings', 'Core'],
    equipment: ['None'],
    difficulty: 'beginner',
    instructions: [
      'Lie on back with knees bent and feet flat',
      'Place arms at sides',
      'Lift hips up toward ceiling',
      'Squeeze glutes at the top',
      'Lower hips back to ground'
    ],
    tips: ['Don\'t arch your back', 'Focus on glute activation'],
    variations: ['Single Leg Bridge', 'Weighted Bridge', 'Marching Bridge'],
    isPublic: true
  },
  {
    id: 'side-plank',
    name: 'Side Plank',
    description: 'A core exercise that targets the obliques and improves stability.',
    category: 'strength',
    muscleGroups: ['Core', 'Obliques', 'Shoulders'],
    equipment: ['None'],
    difficulty: 'intermediate',
    instructions: [
      'Lie on your side with legs stacked',
      'Prop up on your forearm',
      'Lift hips off the ground',
      'Keep body in a straight line',
      'Hold position and breathe'
    ],
    tips: ['Don\'t let hips sag', 'Keep core engaged'],
    variations: ['Side Plank with Leg Lift', 'Side Plank Crunch', 'Advanced Side Plank'],
    isPublic: true
  },

  // CARDIO EXERCISES
  {
    id: 'jumping-jack',
    name: 'Jumping Jack',
    description: 'A classic cardio exercise that gets your heart rate up quickly.',
    category: 'cardio',
    muscleGroups: ['Cardiovascular', 'Full Body'],
    equipment: ['None'],
    difficulty: 'beginner',
    instructions: [
      'Stand with feet together and arms at sides',
      'Jump feet apart while raising arms overhead',
      'Jump back to starting position',
      'Repeat at a steady pace'
    ],
    tips: ['Land softly', 'Keep good posture'],
    variations: ['Low Impact Jack', 'Star Jack', 'Cross Jack'],
    isPublic: true
  },
  {
    id: 'high-knee',
    name: 'High Knees',
    description: 'A running-in-place exercise that improves cardiovascular fitness.',
    category: 'cardio',
    muscleGroups: ['Cardiovascular', 'Quadriceps', 'Core'],
    equipment: ['None'],
    difficulty: 'beginner',
    instructions: [
      'Stand in place and start running',
      'Bring knees up toward your chest',
      'Pump your arms as you would when running',
      'Maintain a steady rhythm'
    ],
    tips: ['Stay on balls of feet', 'Keep core engaged'],
    variations: ['Slow High Knees', 'High Knees with Twist'],
    isPublic: true
  },
  {
    id: 'jump-rope',
    name: 'Jump Rope',
    description: 'An excellent cardio exercise that also improves coordination.',
    category: 'cardio',
    muscleGroups: ['Cardiovascular', 'Calves', 'Shoulders'],
    equipment: ['Jump Rope'],
    difficulty: 'beginner',
    instructions: [
      'Hold rope handles at hip level',
      'Swing rope over your head',
      'Jump as rope comes under your feet',
      'Land softly and continue rhythm'
    ],
    tips: ['Start slow', 'Keep elbows close to body'],
    variations: ['Single Leg Jump', 'Double Unders', 'Side Swings'],
    isPublic: true
  },
  {
    id: 'mountain-climber-cardio',
    name: 'Mountain Climber (Cardio)',
    description: 'A high-intensity cardio variation of the mountain climber.',
    category: 'cardio',
    muscleGroups: ['Cardiovascular', 'Core', 'Shoulders'],
    equipment: ['None'],
    difficulty: 'intermediate',
    instructions: [
      'Start in plank position',
      'Drive knees rapidly toward chest',
      'Alternate legs quickly',
      'Maintain plank position',
      'Keep breathing steady'
    ],
    tips: ['Don\'t let hips move', 'Focus on speed'],
    variations: ['Slow Mountain Climber', 'Cross-body Climber'],
    isPublic: true
  },
  {
    id: 'butterfly-kick',
    name: 'Butterfly Kick',
    description: 'A swimming-inspired cardio exercise that targets core and legs.',
    category: 'cardio',
    muscleGroups: ['Cardiovascular', 'Core', 'Legs'],
    equipment: ['None'],
    difficulty: 'beginner',
    instructions: [
      'Lie on your back with arms at sides',
      'Lift legs slightly off ground',
      'Move legs in a scissor motion',
      'Keep core engaged',
      'Maintain steady rhythm'
    ],
    tips: ['Don\'t let back arch', 'Keep legs straight'],
    variations: ['Single Leg Kick', 'Weighted Kick', 'Flutter Kick'],
    isPublic: true
  },
  {
    id: 'jumping-squat',
    name: 'Jumping Squat',
    description: 'A plyometric exercise that combines strength and cardio.',
    category: 'cardio',
    muscleGroups: ['Cardiovascular', 'Quadriceps', 'Glutes'],
    equipment: ['None'],
    difficulty: 'intermediate',
    instructions: [
      'Start in squat position',
      'Explosively jump up',
      'Land softly in squat position',
      'Immediately repeat',
      'Keep good form'
    ],
    tips: ['Land quietly', 'Don\'t let knees cave'],
    variations: ['Tuck Jump', 'Box Jump', 'Split Jump'],
    isPublic: true
  },

  // FLEXIBILITY EXERCISES
  {
    id: 'cobra-stretch',
    name: 'Cobra Stretch',
    description: 'A gentle back stretch that improves spinal flexibility.',
    category: 'flexibility',
    muscleGroups: ['Back', 'Chest', 'Shoulders'],
    equipment: ['None'],
    difficulty: 'beginner',
    instructions: [
      'Lie face down with hands under shoulders',
      'Press into hands to lift chest off ground',
      'Keep hips and legs on the ground',
      'Hold the stretch and breathe deeply'
    ],
    tips: ['Don\'t overextend', 'Keep neck neutral'],
    variations: ['Sphinx Pose', 'Upward Dog'],
    isPublic: true
  },
  {
    id: 'child-pose',
    name: 'Child\'s Pose',
    description: 'A relaxing stretch that gently stretches the back and hips.',
    category: 'flexibility',
    muscleGroups: ['Back', 'Hips', 'Shoulders'],
    equipment: ['None'],
    difficulty: 'beginner',
    instructions: [
      'Kneel on the ground with knees apart',
      'Sit back on your heels',
      'Fold forward, extending arms in front',
      'Rest forehead on the ground'
    ],
    tips: ['Breathe deeply', 'Relax into the stretch'],
    variations: ['Extended Child\'s Pose', 'Thread the Needle'],
    isPublic: true
  },
  {
    id: 'cat-cow',
    name: 'Cat-Cow Stretch',
    description: 'A gentle spinal movement that improves back flexibility.',
    category: 'flexibility',
    muscleGroups: ['Back', 'Core', 'Neck'],
    equipment: ['None'],
    difficulty: 'beginner',
    instructions: [
      'Start on hands and knees',
      'Arch back up like a cat (Cow)',
      'Round back down like a cow (Cat)',
      'Move slowly with your breath',
      'Repeat for several cycles'
    ],
    tips: ['Move with breath', 'Don\'t force the movement'],
    variations: ['Seated Cat-Cow', 'Standing Cat-Cow'],
    isPublic: true
  },
  {
    id: 'butterfly-stretch',
    name: 'Butterfly Stretch',
    description: 'A seated stretch that opens the hips and inner thighs.',
    category: 'flexibility',
    muscleGroups: ['Hips', 'Inner Thighs', 'Groin'],
    equipment: ['None'],
    difficulty: 'beginner',
    instructions: [
      'Sit on ground with soles of feet together',
      'Gently press knees toward ground',
      'Lean forward from hips',
      'Hold stretch and breathe',
      'Don\'t force the stretch'
    ],
    tips: ['Keep back straight', 'Don\'t bounce'],
    variations: ['Forward Fold', 'Seated Straddle'],
    isPublic: true
  },
  {
    id: 'hamstring-stretch',
    name: 'Hamstring Stretch',
    description: 'A stretch that improves flexibility in the back of the thighs.',
    category: 'flexibility',
    muscleGroups: ['Hamstrings', 'Lower Back'],
    equipment: ['None'],
    difficulty: 'beginner',
    instructions: [
      'Sit on ground with legs extended',
      'Reach forward toward toes',
      'Keep back straight',
      'Hold stretch and breathe',
      'Don\'t round your back'
    ],
    tips: ['Keep back straight', 'Feel stretch in hamstrings'],
    variations: ['Standing Hamstring Stretch', 'Lying Hamstring Stretch'],
    isPublic: true
  },

  // BALANCE EXERCISES
  {
    id: 'tree-pose',
    name: 'Tree Pose',
    description: 'A yoga pose that improves balance and focus.',
    category: 'balance',
    muscleGroups: ['Core', 'Legs', 'Balance'],
    equipment: ['None'],
    difficulty: 'beginner',
    instructions: [
      'Stand on one leg with other foot on inner thigh',
      'Bring hands to prayer position at chest',
      'Focus on a point in front of you',
      'Hold the pose while breathing steadily'
    ],
    tips: ['Start near a wall for support', 'Don\'t lock your standing knee'],
    variations: ['Half Tree Pose', 'Tree Pose with Arms Up'],
    isPublic: true
  },
  {
    id: 'single-leg-stand',
    name: 'Single Leg Stand',
    description: 'A simple balance exercise that improves stability.',
    category: 'balance',
    muscleGroups: ['Core', 'Legs', 'Balance'],
    equipment: ['None'],
    difficulty: 'beginner',
    instructions: [
      'Stand on one leg',
      'Lift other leg slightly off ground',
      'Keep standing leg slightly bent',
      'Hold position and breathe',
      'Focus on a point ahead'
    ],
    tips: ['Don\'t lock your knee', 'Keep core engaged'],
    variations: ['Single Leg with Eyes Closed', 'Single Leg with Arm Movement'],
    isPublic: true
  },
  {
    id: 'heel-to-toe-walk',
    name: 'Heel-to-Toe Walk',
    description: 'A walking exercise that improves balance and coordination.',
    category: 'balance',
    muscleGroups: ['Core', 'Legs', 'Balance'],
    equipment: ['None'],
    difficulty: 'beginner',
    instructions: [
      'Place heel of one foot in front of toes of other',
      'Walk in a straight line',
      'Keep arms out for balance',
      'Focus on a point ahead',
      'Take small, controlled steps'
    ],
    tips: ['Go slowly', 'Don\'t look down'],
    variations: ['Backward Heel-to-Toe', 'Sideways Walk'],
    isPublic: true
  }
];

// Export the main exercises array (original + generated)
export const exercises = [...originalExercises, ...generatedExercises];

export const exerciseCategories = [
  { id: 'all', label: 'All Exercises', icon: '🏃' },
  { id: 'strength', label: 'Strength', icon: '💪' },
  { id: 'cardio', label: 'Cardio', icon: '❤️' },
  { id: 'flexibility', label: 'Flexibility', icon: '🧘' },
  { id: 'balance', label: 'Balance', icon: '⚖️' }
];

export const muscleGroups = [
  'Full Body', 'Core', 'Upper Body', 'Lower Body',
  'Chest', 'Back', 'Shoulders', 'Biceps', 'Triceps',
  'Quadriceps', 'Hamstrings', 'Glutes', 'Calves',
  'Cardiovascular', 'Obliques', 'Neck', 'Forearms'
];

export const equipmentTypes = [
  'None', 'Dumbbells', 'Barbell', 'Kettlebell', 'Resistance Bands',
  'Pull-up Bar', 'Bench', 'Jump Rope', 'Yoga Mat', 'Foam Roller',
  'Medicine Ball', 'Stability Ball', 'TRX'
];

export const difficultyLevels = [
  { id: 'all', label: 'All Levels' },
  { id: 'beginner', label: 'Beginner' },
  { id: 'intermediate', label: 'Intermediate' },
  { id: 'advanced', label: 'Advanced' }
];
